// Ticket detail - a Jira two-pane view over one board card.
//  - Left: the (pre-sliced) card body + interactive embeds, then the Workflow
//    action, then the comment thread.
//  - Right: Details rail (status, assignee, reporter, points, labels, sprint).
//
// Everything is DB-backed: the card body, thread (scripted + system + user
// comments) and state come from GET /api/simulations/:slug/cards/:key. Resolve,
// QA submit, credential requests and posting a comment all hit the API. The
// credentials exchange and QA verdict arrive as persisted comments in the thread
// (QA verdicts are written by the server; the credential secret is injected at
// render by decrypting the stored credential - never persisted in the comment).
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FiArrowLeft,
  FiExternalLink,
  FiKey,
  FiCheckCircle,
  FiCopy,
  FiCheck,
  FiEye,
  FiEyeOff,
  FiFilter,
} from "react-icons/fi";
import {
  getCard,
  startCard,
  resolveCard,
  submitQa,
  // postComment,  // free-text commenting disabled for now (see composer below)
  requestCredentials,
  clearCard,
} from "../../features/simulation/simulationSlice";
import IntegrationActionButton from "../Lesson/IntegrationActionButton";
import { parseLessonParts } from "./mithraLessonParser";
import { useSimulation } from "./SimulationContext";
import { Avatar, Label, StatusPill } from "./mithraUi";
import MithraMarkdown from "./mithraMarkdown";

// --- Inline embeds (stand-ins for {{...}} placeholders in the body) ---------

function CredentialsPointer() {
  return (
    <div className="mi-cred-pointer">
      <FiKey />
      <span>
        Your <code>client_id</code> / <code>client_secret</code> come from the partner, not this
        page. <strong>Request them from Cyrus in the comments below.</strong>
      </span>
    </div>
  );
}

function QaPointer() {
  return (
    <div className="mi-cred-pointer">
      <FiCheckCircle />
      <span>
        When your build is ready, <strong>Submit to QA</strong> from the Workflow panel below. The QA team will test your work and the Architect will reply to you in a comment to let you know if QA passed or not.
      </span>
    </div>
  );
}

function Embed({ part }) {
  if (part.type === "action") {
    if (part.action === "credentials") return <CredentialsPointer />;
    if (part.action === "install-schema")
      return <IntegrationActionButton action="install-schema" label="Install Order Schema" />;
    if (part.action === "install-data")
      return (
        <IntegrationActionButton action="install-data" dataKey={part.dataKey} label="Install Sample Data" />
      );
  }
  if (part.type === "task") return <QaPointer />;
  return null;
}

// --- Credential field (reveal + copy) --------------------------------------

function CredentialField({ label, value, masked }) {
  const [hidden, setHidden] = useState(masked);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Error copying to clipboard:", err);
    }
  };

  const display = masked && hidden ? "•".repeat(Math.min(value?.length || 24, 40)) : value;

  return (
    <div className="mi-cred-field">
      <label>{label}</label>
      <div className="mi-cred-value">
        <span className="val">{display}</span>
        {masked && (
          <button type="button" onClick={() => setHidden(!hidden)} title={hidden ? "Show" : "Hide"}>
            {hidden ? <FiEye /> : <FiEyeOff />}
          </button>
        )}
        <button type="button" onClick={handleCopy} title="Copy to clipboard">
          {copied ? <FiCheck style={{ color: "#006644" }} /> : <FiCopy />}
        </button>
      </div>
    </div>
  );
}

// --- Comment rows ----------------------------------------------------------

function CommentRow({ person, children }) {
  if (!person) return null;
  return (
    <div className="mi-comment">
      <Avatar person={person} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div>
          <span className="who">{person.name}</span>
          {person.role && (
            <span className="role">
              {person.role} · {person.team}
            </span>
          )}
        </div>
        <div className="text">{children}</div>
      </div>
    </div>
  );
}

function ThreadComment({ comment, peopleByKey, you }) {
  const person = comment.authorPersonKey
    ? peopleByKey[comment.authorPersonKey] || (comment.kind === "user" ? you : null)
    : you;

  return (
    <CommentRow person={person}>
      <MithraMarkdown>{comment.body}</MithraMarkdown>
      {comment.meta && comment.meta.credentialReply && comment.credentials && (
        <div className="mi-cred-fields" style={{ marginTop: 10 }}>
          <CredentialField label="client_id" value={comment.credentials.clientId} masked={false} />
          <CredentialField label="client_secret" value={comment.credentials.clientSecret} masked />
        </div>
      )}
    </CommentRow>
  );
}

function DetailsRow({ k, children }) {
  return (
    <div className="row">
      <div className="k">{k}</div>
      <div className="v">{children}</div>
    </div>
  );
}

// --- Ticket -----------------------------------------------------------------

function MithraTicket() {
  const { slug, cardKey } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { company, peopleByKey, you } = useSimulation();
  const { card: cardData, qa, isRequestingCredentials } = useSelector(
    (s) => s.simulation
  );
  // Free-text commenting is disabled for now.
  // const [draft, setDraft] = useState("");

  const base = `/simulations/${slug}`;

  useEffect(() => {
    dispatch(getCard({ slug, cardKey }));
    // Opening a ticket "picks it up" -> In Progress (unless already further along).
    dispatch(startCard({ slug, cardKey }));
    return () => dispatch(clearCard());
  }, [dispatch, slug, cardKey]);

  const card = cardData && cardData.card && cardData.card.key === cardKey ? cardData.card : null;
  const state = (cardData && cardData.state) || null;
  const thread = (cardData && cardData.thread) || [];

  const parts = useMemo(() => {
    if (!card) return [];
    return parseLessonParts(card.bodyMarkdown || "");
  }, [card]);

  if (!card) {
    return (
      <div>
        <div className="mi-crumbs">
          <Link to={`${base}/board`}>
            <FiArrowLeft style={{ display: "inline", verticalAlign: "-2px", marginRight: 4 }} />
            {company.projectKey} board
          </Link>
        </div>
        <div className="mi-empty">Loading ticket…</div>
      </div>
    );
  }

  const status = qa.submitting ? "qa" : (state && state.status) || "todo";
  const done = status === "done";
  const reporter = peopleByKey[card.reporterKey] || peopleByKey.Saman;

  // Credentials composer shows only until the exchange exists in the thread.
  const hasCredentialExchange = thread.some((c) => c.meta && c.meta.credentialReply);
  const showCredentialsComposer = !!card.needsCredentials && !hasCredentialExchange;

  const handleResolve = () => dispatch(resolveCard({ slug, cardKey }));

  const handleQa = async () => {
    try {
      await dispatch(submitQa({ slug, cardKey })).unwrap();
    } catch (e) {
      /* rejected handled in slice */
    }
    // Refresh thread + state so the QA verdict comment appears.
    dispatch(getCard({ slug, cardKey }));
  };

  const handleRequestCredentials = () => dispatch(requestCredentials({ slug, cardKey }));

  // Free-text commenting is disabled for now. The only learner-authored comment
  // that remains is the prewritten credentials request (handleRequestCredentials).
  // const handlePostComment = async () => {
  //   const body = draft.trim();
  //   if (!body) return;
  //   try {
  //     await dispatch(postComment({ slug, cardKey, body })).unwrap();
  //     setDraft("");
  //   } catch (e) {
  //     /* keep draft on failure */
  //   }
  // };

  const qaRetry = qa.verdict === "fail" || qa.verdict === "error";

  return (
    <div>
      <div className="mi-crumbs">
        <Link to={`${base}/board`}>
          <FiArrowLeft style={{ display: "inline", verticalAlign: "-2px", marginRight: 4 }} />
          {company.projectKey} board
        </Link>
      </div>

      <div className="mi-ticket">
        {/* Left: description + work */}
        <div>
          <div className="mi-ticket-key">
            <span
              className="mi-type"
              style={{ background: card.type === "spike" ? "#6554c0" : "#2684ff" }}
            >
              {card.type === "spike" ? "◆" : "✓"}
            </span>
            {card.key}
            <span style={{ color: "var(--mi-text-faint)", fontWeight: 400 }}>
              · {card.type === "spike" ? "Spike" : "Task"}
            </span>
          </div>
          <div className="mi-ticket-title">{card.title}</div>

          <div className="mi-section-label" style={{ marginTop: 0 }}>
            Description
          </div>
          {parts.map((part, i) =>
            part.type === "markdown" ? (
              <MithraMarkdown key={i}>{part.content}</MithraMarkdown>
            ) : (
              <Embed key={i} part={part} />
            )
          )}

          {/* Spike reading list */}
          {card.reading && card.reading.length > 0 && (
            <>
              <div className="mi-section-label">Further reading</div>
              <ul className="mi-reading">
                {card.reading.map((r, i) => (
                  <li key={i}>
                    <FiExternalLink style={{ color: "var(--mi-primary)" }} />
                    {r.to ? (
                      <Link to={r.to} target="_blank" rel="noopener noreferrer">
                        {r.label}
                      </Link>
                    ) : (
                      <a href={r.href} target="_blank" rel="noopener noreferrer">
                        {r.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Workflow action */}
          <div className="mi-section-label">Workflow</div>
          {card.type === "spike" ? (
            done ? (
              <button className="mi-btn done" disabled>
                ✓ Resolved
              </button>
            ) : (
              <button className="mi-btn primary" onClick={handleResolve}>
                Resolve spike
              </button>
            )
          ) : card.completion === "local" ? (
            done ? (
              <button className="mi-btn done" disabled>
                ✓ Done
              </button>
            ) : (
              <>
                <p style={{ color: "var(--mi-text-subtle)", marginBottom: 8 }}>
                  Run the installs above, then mark this ticket done.
                </p>
                <button className="mi-btn primary" onClick={handleResolve}>
                  Mark as Done
                </button>
              </>
            )
          ) : done ? (
            <button className="mi-btn done" disabled>
              ✓ Done - QA passed
            </button>
          ) : (
            <>
              <p style={{ color: "var(--mi-text-subtle)", marginBottom: 8 }}>
                Built everything above? <strong>Submit to QA</strong> - the checks run against your
                org and the architect drops the verdict in the comments below.
              </p>
              <button className="mi-btn primary" onClick={handleQa} disabled={qa.submitting}>
                {qa.submitting ? (
                  <>
                    <span className="mi-spinner" aria-hidden="true" /> Submitting to QA…
                  </>
                ) : qaRetry ? (
                  "Resubmit to QA"
                ) : (
                  "Submit to QA"
                )}
              </button>
            </>
          )}

          {/* Comments */}
          <div className="mi-section-label mi-comments-label">
            <span>Comments</span>
            <span
              className="mi-comments-filter"
              title="Sorted oldest to newest"
              aria-label="Sorted oldest to newest"
            >
              <FiFilter aria-hidden="true" />
              Oldest to newest
            </span>
          </div>
          <div className="mi-panel" style={{ padding: "4px 16px 12px" }}>
            {thread.map((c) => (
              <ThreadComment key={c.id} comment={c} peopleByKey={peopleByKey} you={you} />
            ))}

            {qa.submitting && (
              <CommentRow person={peopleByKey.Saman}>
                <span className="mi-typing">QA is reviewing your ticket…</span>
              </CommentRow>
            )}

            {/* Credentials request composer */}
            {showCredentialsComposer && (
              <div className="mi-comment mi-cred-composer">
                {you && <Avatar person={you} size="sm" />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="mi-cred-composer-label">You</div>
                  <p style={{ margin: "4px 0 8px", color: "var(--mi-text-subtle)" }}>
                    You need a <code>client_id</code> / <code>client_secret</code> from the partner.
                    Ask Cyrus for them.
                  </p>
                  <div className="mi-cred-composer-actions">
                    <button
                      className="mi-btn primary"
                      onClick={handleRequestCredentials}
                      disabled={isRequestingCredentials}
                    >
                      {isRequestingCredentials ? "Sending…" : "Ask Cyrus for credentials"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Post a comment - free-text commenting disabled for now. The
                prewritten credentials request above is the only learner-authored
                comment that remains. To re-enable, restore this block plus the
                postComment import, draft state, and handlePostComment handler. */}
            {/*
            <div className="mi-comment mi-cred-composer">
              {you && <Avatar person={you} size="sm" />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <textarea
                  className="mi-comment-input"
                  placeholder="Add a comment…"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={2}
                />
                <div className="mi-cred-composer-actions">
                  <button
                    className="mi-btn primary"
                    onClick={handlePostComment}
                    disabled={isPostingComment || !draft.trim()}
                  >
                    {isPostingComment ? "Posting…" : "Comment"}
                  </button>
                </div>
              </div>
            </div>
            */}
          </div>
        </div>

        {/* Right: details rail */}
        <div>
          <div className="mi-panel mi-details">
            <div style={{ padding: "10px 14px 4px" }}>
              <StatusPill status={status} />
            </div>
            <DetailsRow k="Assignee">
              <span className="mi-person">
                {you && <Avatar person={you} size="sm" />} You
              </span>
            </DetailsRow>
            <DetailsRow k="Reporter">
              <span className="mi-person">
                {reporter && <Avatar person={reporter} size="sm" />} {reporter && reporter.name}
              </span>
            </DetailsRow>
            <DetailsRow k="Type">{card.type === "spike" ? "Spike" : "Task"}</DetailsRow>
            <DetailsRow k="Points">{card.storyPoints}</DetailsRow>
            <DetailsRow k="Labels">
              {card.labels.map((l) => (
                <Label key={l} text={l} />
              ))}
            </DetailsRow>
            {/* <DetailsRow k="Sprint">{company.sprint}</DetailsRow>
            <DetailsRow k="Project">{company.projectKey}</DetailsRow> */}
          </div>

          <button
            className="mi-btn subtle"
            style={{ marginTop: 12, width: "100%" }}
            onClick={() => navigate(`${base}/board`)}
          >
            Back to board
          </button>
        </div>
      </div>
    </div>
  );
}

export default MithraTicket;
